import {
  BellRing,
  Building2,
  CalendarRange,
  CreditCard,
  IdCard,
  Landmark,
  MailCheck,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Send,
  ShieldCheck,
  Terminal,
  Timer,
  Trash2,
  Wallet,
} from 'lucide-react';
import { useEffect, useId, type ReactNode, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SelectField,
  useToast,
} from '@/components/ui';
import { MONTHS } from '@/components/ui/DatePicker';
import { useCompanySettings, useUpdateCompanySettings } from '@/hooks/useCompanySettings';
import {
  type BankAccount,
  type EmailSender,
  CompanySettingsData,
} from '@/services/companySettings.service';

interface SectionProps {
  icon: typeof Building2;
  title: string;
  children: ReactNode;
}

const Section = ({ icon: Icon, title, children }: SectionProps) => (
  <Card className="mt-6">
    <div className="mb-4 flex items-center gap-2 border-b border-line pb-3">
      <Icon className="h-4 w-4 text-brand" />
      <p className="text-sm font-bold text-ink">{title}</p>
    </div>
    {children}
  </Card>
);

const MONTH_OPTIONS = MONTHS.map((label, i) => ({ value: String(i + 1), label }));

/**
 * Coerces a stored month to the exact form MONTH_OPTIONS uses.
 *
 * The setting is a 1-2 digit string, so '4' and '04' are both legal and mean
 * April - but only '4' matches an option. Anything unusable falls back to 4,
 * the Indian FY start the dashboard also defaults to.
 */
const normalizeMonth = (raw: string | undefined): string => {
  const month = Number(raw);
  return Number.isInteger(month) && month >= 1 && month <= 12 ? String(month) : '4';
};

// Mirrors the server's DESIGNATION_DEFAULTS. Shown when the setting has never
// been saved, so the form states the vocabulary that is actually in effect
// rather than two empty lists that read as "no options configured".
const DESIGNATION_LEVEL_DEFAULTS = ['Senior', 'Mid-Level', 'Junior'];
const DESIGNATION_DEPT_DEFAULTS = ['Sales', 'Billing', 'Renewal', 'Service', 'Admin'];

const parseLabelList = (raw: string | undefined, fallback: string[]): string[] => {
  try {
    const parsed: unknown = JSON.parse(raw ?? 'null');
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed.every((item) => typeof item === 'string')
    ) {
      return parsed;
    }
  } catch {
    /* fall through to the default */
  }
  return [...fallback];
};

interface LabelListEditorProps {
  legend: string;
  hint: string;
  values: string[];
  onChange: (next: string[]) => void;
}

/**
 * An editable list of short labels, used for both designation dropdowns.
 *
 * The last remaining row cannot be deleted: an empty list is rejected by the
 * server (it would leave the onboarding form with a dropdown nobody can answer),
 * and refusing the delete is a clearer statement of that rule than a save-time
 * error would be.
 */
const LabelListEditor = ({ legend, hint, values, onChange }: LabelListEditorProps) => (
  <fieldset className="min-w-0">
    <legend className="text-sm font-semibold text-ink">{legend}</legend>
    <p className="mb-2 mt-1 text-xs text-ink/50">{hint}</p>
    <div className="flex flex-col gap-2">
      {values.map((value, index) => (
        <div
          /* Index-keyed on purpose: the label itself is the value being edited,
             so keying on it would remount the input on every keystroke and lose
             focus. The list is reordered only by add/remove at the ends. */
          // eslint-disable-next-line react/no-array-index-key
          key={`${legend}-${index}`}
          className="flex items-center gap-2"
        >
          <Input
            value={value}
            aria-label={`${legend} option ${index + 1}`}
            onChange={(e) => onChange(values.map((v, i) => (i === index ? e.target.value : v)))}
          />
          <button
            type="button"
            aria-label={`Remove ${value || `option ${index + 1}`}`}
            disabled={values.length <= 1}
            onClick={() => onChange(values.filter((_unused, i) => i !== index))}
            className="shrink-0 rounded-lg border border-line bg-white p-2 text-ink/50 transition-colors hover:bg-cream hover:text-danger disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
    <button
      type="button"
      onClick={() => onChange([...values, ''])}
      className="mt-2 flex items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink shadow-sm transition-colors hover:bg-cream"
    >
      <Plus className="h-3.5 w-3.5" />
      Add option
    </button>
  </fieldset>
);

// Mirrors OPS_ALERT_COOLDOWN_HOURS.default on the server. Shown when the key has
// never been saved, so the form states the cooldown that is actually in effect
// rather than leaving a blank that reads as "no suppression".
const OPS_ALERT_COOLDOWN_DEFAULT = 6;

const EMPTY_BANK = (): BankAccount => ({
  label: '',
  accountName: '',
  bankName: '',
  accountNumber: '',
  ifscCode: '',
  upiId: '',
  razorpayLink: '',
});

const EMPTY_SENDER = (): EmailSender => ({
  label: '',
  email: '',
  fromName: '',
});

const parseSenders = (raw: string | undefined): EmailSender[] => {
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0) return parsed as EmailSender[];
    } catch {
      // fall through
    }
  }
  return [EMPTY_SENDER()];
};

const parseBanks = (
  raw: string | undefined,
  fallback: Partial<CompanySettingsData>,
): BankAccount[] => {
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0) return parsed as BankAccount[];
    } catch {
      // fall through
    }
  }
  if (fallback.bank_name || fallback.bank_account_number || fallback.bank_account_name) {
    return [
      {
        label: fallback.bank_name ?? '',
        accountName: fallback.bank_account_name ?? '',
        bankName: fallback.bank_name ?? '',
        accountNumber: fallback.bank_account_number ?? '',
        ifscCode: fallback.bank_ifsc_code ?? '',
        upiId: fallback.bank_upi_id ?? '',
        razorpayLink: fallback.razorpay_link ?? '',
      },
    ];
  }
  return [EMPTY_BANK()];
};

interface DialogFieldProps {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (val: string) => void;
}

const DialogField = ({ label, value, placeholder, onChange }: DialogFieldProps) => {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full rounded-md border border-line bg-white px-3 py-2 text-sm shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
      />
    </div>
  );
};

interface BankDialogProps {
  bank: BankAccount | null;
  open: boolean;
  canDelete: boolean;
  onClose: () => void;
  onSave: (bank: BankAccount) => void;
  onDelete: () => void;
}

const BankDialog = ({ bank, open, canDelete, onClose, onSave, onDelete }: BankDialogProps) => {
  const [draft, setDraft] = useState<BankAccount>(EMPTY_BANK());

  useEffect(() => {
    if (open) setDraft(bank ?? EMPTY_BANK());
  }, [open, bank]);

  const set = (field: keyof BankAccount) => (val: string) => {
    setDraft((prev) => ({ ...prev, [field]: val }));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{bank ? bank.label || 'Edit bank account' : 'Add bank account'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2 sm:grid-cols-3">
          <DialogField
            label="Bank label"
            value={draft.label}
            placeholder="e.g. SBI, HDFC, Axis"
            onChange={set('label')}
          />
          <div className="sm:col-span-2">
            <DialogField
              label="Bank name"
              value={draft.bankName}
              placeholder="State Bank of India"
              onChange={set('bankName')}
            />
          </div>
          <DialogField
            label="Account number"
            value={draft.accountNumber}
            placeholder="Account number"
            onChange={set('accountNumber')}
          />
          <div className="sm:col-span-2">
            <DialogField
              label="Account name"
              value={draft.accountName}
              placeholder="Gamechanger Business Services (I) Pvt. Ltd."
              onChange={set('accountName')}
            />
          </div>
          <DialogField
            label="IFSC code"
            value={draft.ifscCode}
            placeholder="SBIN0000000"
            onChange={set('ifscCode')}
          />
          <div className="sm:col-span-2">
            <DialogField
              label="UPI ID"
              value={draft.upiId}
              placeholder="company.upi@bank"
              onChange={set('upiId')}
            />
          </div>
          <div className="sm:col-span-3">
            <DialogField
              label="RazorPay payment link"
              value={draft.razorpayLink}
              placeholder="https://razorpay.me/@..."
              onChange={set('razorpayLink')}
            />
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="mr-auto flex items-center gap-1.5 text-sm font-semibold text-danger transition-colors hover:text-danger/80"
            >
              <Trash2 className="h-4 w-4" />
              Remove bank
            </button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={() => onSave(draft)}>
            Save bank
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminCompanySettingsPage = () => {
  const { data: settings, isLoading } = useCompanySettings();
  const update = useUpdateCompanySettings();
  const { toast } = useToast();

  const [banks, setBanks] = useState<BankAccount[]>([EMPTY_BANK()]);
  const [dialogIndex, setDialogIndex] = useState<number | null>(null);
  const [senders, setSenders] = useState<EmailSender[]>([EMPTY_SENDER()]);
  const [levels, setLevels] = useState<string[]>(DESIGNATION_LEVEL_DEFAULTS);
  const [departments, setDepartments] = useState<string[]>(DESIGNATION_DEPT_DEFAULTS);

  const { register, handleSubmit, reset, control, setValue } = useForm<CompanySettingsData>({
    defaultValues: {},
  });

  const installmentDaysValue = useWatch({
    control,
    name: 'payment_installment_days',
  });
  const INSTALLMENT_PRESETS = ['30', '60', '90'] as const;

  useEffect(() => {
    if (settings) {
      reset({
        ...settings,
        // Show the Indian FY default (1 April) when unset so the Start month
        // Select doesn't display a misleading January (the dashboard uses the
        // same fallback).
        //
        // Normalised, because the option values are '1'..'12' and the column
        // accepts \d{1,2}: a stored '04' is a perfectly valid month that would
        // match no option, and the Select would then show nothing at all.
        financial_year_start_month: normalizeMonth(settings.financial_year_start_month),
        financial_year_start_day: settings.financial_year_start_day || '1',
        // The server GET already resolves otp_delivery_mode to its effective value
        // (env-derived when unset), so just fall back to 'sms' defensively.
        otp_delivery_mode: settings.otp_delivery_mode || 'sms',
        // Default the T&C compliance gate to On when unset (matches the server
        // getter, which treats anything but 'false' as enforcing).
        tc_enforce_sent_before_accept: settings.tc_enforce_sent_before_accept || 'true',
        // Same treatment as its siblings, and for the same reason: getOpsAlertConfig
        // treats anything but 'false' as On, so an unset key must show as On. It
        // happened to render correctly only because "On" is the first <option> and a
        // native select falls back to that - reorder the two options and the form
        // would start claiming alerts were Off while the server sent them.
        ops_alert_enabled: settings.ops_alert_enabled || 'true',
        // Blank here would read as "no cooldown" while the server applies its
        // 6-hour default, which is the same mismatch the write-side clamp removes.
        ops_alert_cooldown_hours:
          settings.ops_alert_cooldown_hours || String(OPS_ALERT_COOLDOWN_DEFAULT),
      });
      setBanks(parseBanks(settings.bank_accounts, settings));
      setSenders(parseSenders(settings.email_senders));
      setLevels(parseLabelList(settings.designation_prefixes, DESIGNATION_LEVEL_DEFAULTS));
      setDepartments(parseLabelList(settings.designation_roles, DESIGNATION_DEPT_DEFAULTS));
    }
  }, [settings, reset]);

  const onAddSender = (): void => setSenders((prev) => [...prev, EMPTY_SENDER()]);
  const onChangeSender = (index: number, field: keyof EmailSender, value: string): void => {
    setSenders((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };
  const onRemoveSender = (index: number): void => {
    setSenders((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const openDialog = (index: number): void => setDialogIndex(index);
  const closeDialog = (): void => setDialogIndex(null);

  const onAddBank = (): void => {
    const newIndex = banks.length;
    setBanks((prev) => [...prev, EMPTY_BANK()]);
    setDialogIndex(newIndex);
  };

  const onSaveBank = (updated: BankAccount): void => {
    if (dialogIndex === null) return;
    setBanks((prev) => prev.map((b, i) => (i === dialogIndex ? updated : b)));
    closeDialog();
  };

  const onDeleteBank = (): void => {
    if (dialogIndex === null) return;
    setBanks((prev) => prev.filter((_, i) => i !== dialogIndex));
    closeDialog();
  };

  const onSubmit = (values: CompanySettingsData): void => {
    // A blank FY day falls back to day 1; the month Select always carries a
    // value. The server clamps anything out of range as a backstop.
    const day = values.financial_year_start_day?.trim() || '1';

    // Blank rows are dropped rather than saved: an empty label would render as
    // a nameless option in the onboarding dropdown. Refused outright when that
    // leaves a list empty or duplicated, which is exactly what the server's own
    // validator rejects - catching it here names WHICH list is wrong instead of
    // failing the whole save with one generic message.
    const cleanLevels = levels.map((v) => v.trim()).filter(Boolean);
    const cleanDepartments = departments.map((v) => v.trim()).filter(Boolean);
    const distinct = (list: string[]): boolean =>
      new Set(list.map((v) => v.toLowerCase())).size === list.length;
    if (cleanLevels.length === 0 || cleanDepartments.length === 0) {
      toast.error('Designation levels and departments each need at least one option.');
      return;
    }
    if (!distinct(cleanLevels) || !distinct(cleanDepartments)) {
      toast.error('Designation options must be distinct.');
      return;
    }

    update.mutate(
      {
        ...values,
        financial_year_start_day: day,
        bank_accounts: JSON.stringify(banks),
        email_senders: JSON.stringify(senders.filter((s) => s.email.trim())),
        designation_prefixes: JSON.stringify(cleanLevels),
        designation_roles: JSON.stringify(cleanDepartments),
      },
      {
        onSuccess: () => toast.success('Settings saved'),
        onError: () => toast.error('Could not save settings. Please try again.'),
      },
    );
  };

  if (isLoading) {
    return <p className="text-sm text-ink/50">Loading settings…</p>;
  }

  const activeBank = dialogIndex !== null ? banks[dialogIndex] : null;

  return (
    <form
      onSubmit={(e) => {
        handleSubmit(onSubmit)(e).catch(() => {});
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            Company Settings
          </h1>
          <p className="mt-1 text-sm text-ink/50">
            Configure company-wide values used in email templates and billing documents.
          </p>
        </div>
        <Button type="submit" disabled={update.isPending} className="flex items-center gap-1.5">
          <Save className="h-4 w-4" />
          {update.isPending ? 'Saving…' : 'Save settings'}
        </Button>
      </div>

      <Section icon={Building2} title="Company Information">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Input
              label="Legal Company Name"
              placeholder="Gamechanger Business Services (I) Pvt. Ltd."
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('company_legal_name')}
            />
          </div>
          <div className="sm:col-span-2">
            <Input
              label="Company Address"
              placeholder="Office address shown in the email footer"
              helperText="Appears in the branded email footer below the legal name"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('company_address')}
            />
          </div>
          <Input
            label="GST Percentage"
            placeholder="18"
            helperText="Used in quotation totals (e.g. 18)"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('gst_percentage')}
          />
          <Input
            label="Quotation Validity (Days)"
            placeholder="30"
            helperText="Days from send date until quotation expires"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('quotation_validity_days')}
          />
        </div>
      </Section>

      <Section icon={CalendarRange} title="Financial Year">
        <p className="mb-4 text-sm text-ink/50">
          Sets where the financial year starts, used by the FYTD (Financial Year to Date) filter on
          the Sales dashboard. Defaults to 1 April if left unset. The year runs from this date
          through the day before it, one year on.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Controller
            name="financial_year_start_month"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Start month"
                placeholder="Select a month"
                options={MONTH_OPTIONS}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
          <Input
            label="Start day"
            type="text"
            inputMode="numeric"
            maxLength={2}
            placeholder="1"
            helperText="Defaults to 1 if left blank"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('financial_year_start_day')}
          />
        </div>
      </Section>

      <Section icon={Timer} title="Data Retention">
        <p className="mb-4 text-sm text-ink/50">
          How long login &amp; security records are kept before an automatic daily purge deletes
          them. Leave blank for the 90-day default, or set to{' '}
          <span className="font-semibold text-ink/70">0</span> to keep records forever.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Audit log retention (days)"
            type="text"
            inputMode="numeric"
            maxLength={4}
            placeholder="90"
            helperText="OTP & session audit logs. Minimum 90 days (lower is raised to 90); 0 = forever"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('audit_retention_days')}
          />
          <Input
            label="Session retention (days)"
            type="text"
            inputMode="numeric"
            maxLength={4}
            placeholder="90"
            helperText="Expired / revoked device sessions"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('session_retention_days')}
          />
        </div>
      </Section>

      <Section icon={Wallet} title="Payment Schedule">
        <p className="mb-4 text-sm text-ink/50">
          Sets the initial due-date span used when a Sales RM opens the Payment Details step for a
          lead. The RM can edit dates freely after the defaults are seeded. Common choices: 30, 60,
          or 90 days.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Input
              label="Initial payment span (days)"
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="30"
              helperText="Days from today used to spread initial installment dates"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('payment_installment_days')}
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {INSTALLMENT_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setValue('payment_installment_days', preset)}
                  className={`rounded-full border px-3 py-0.5 text-xs font-semibold transition-colors ${
                    installmentDaysValue === preset
                      ? 'border-brand bg-brand text-white'
                      : 'border-line bg-white text-ink hover:bg-cream'
                  }`}
                >
                  {preset} days
                </button>
              ))}
            </div>
          </div>
          <div>
            <Input
              label="Reminder days before due"
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="2"
              helperText="Days before each installment's due date to email the reminder (an on-due reminder is always sent). 0 = only on the due date."
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('installment_reminder_days_before')}
            />
          </div>
        </div>
      </Section>

      <Section icon={RefreshCw} title="Renewal Schedule">
        <p className="mb-4 text-sm text-ink/50">
          When the automated renewal emails go out, relative to each renewal&apos;s due date. The
          notice and follow-ups are sent before the due date; the service termination notice goes
          out after it if the renewal is still unpaid. Leave a field blank to keep its default.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Renewal notice (days before due)"
            type="text"
            inputMode="numeric"
            maxLength={3}
            placeholder="60"
            helperText="First renewal email, sent this many days before the due date. Default 60."
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('renewal_notice_days_before')}
          />
          <Input
            label="Follow-up 1 (days before due)"
            type="text"
            inputMode="numeric"
            maxLength={3}
            placeholder="30"
            helperText="First reminder if still unpaid. Default 30."
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('renewal_followup1_days_before')}
          />
          <Input
            label="Follow-up 2 (days before due)"
            type="text"
            inputMode="numeric"
            maxLength={3}
            placeholder="15"
            helperText="Second reminder if still unpaid. Default 15."
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('renewal_followup2_days_before')}
          />
          <Input
            label="Service termination (days after due)"
            type="text"
            inputMode="numeric"
            maxLength={3}
            placeholder="1"
            helperText="Days past the due date before the termination notice is sent. Default 1."
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('renewal_termination_days_after')}
          />
        </div>
      </Section>

      {/*
        Operational alerts go to the TECH/SUPPORT team about the system itself, never
        to a client. Kept configurable here rather than in env so recipients can be
        changed without a deploy - which matters because the address that should get
        these is the one thing most likely to change as a team grows.
      */}
      <Section icon={BellRing} title="Operational Alerts">
        <p className="mb-4 text-sm text-ink/50">
          Where the system emails your tech / support team when something breaks on its own - today,
          when the scheduled AMFI sync for the MFD dashboard is missed or fails. Leave the address
          blank to turn alerts off entirely.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Input
              label="Alert recipients"
              type="text"
              placeholder="tech@example.com, support@example.com"
              helperText="Comma or semicolon separated, up to 10 addresses. Invalid entries are ignored. Blank = no alerts are sent."
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('ops_alert_emails')}
            />
          </div>
          <Controller
            name="ops_alert_enabled"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Send operational alerts"
                helperText="Off suppresses every alert regardless of the recipients above."
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
              >
                <option value="true">On</option>
                <option value="false">Off</option>
              </SelectField>
            )}
          />
          <Input
            label="Repeat the same alert after (hours)"
            type="text"
            inputMode="numeric"
            maxLength={3}
            placeholder="6"
            helperText="Stops an ongoing problem mailing every minute. An escalation (missed to critical) always sends immediately, whatever this is set to."
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('ops_alert_cooldown_hours')}
          />
        </div>
      </Section>

      <Section icon={IdCard} title="Designations">
        <p className="mb-4 text-xs text-ink/50">
          The two dropdowns every employee picks from when they onboard, and that an admin edits on
          a user&apos;s profile. Adding an option here makes it selectable immediately - no deploy
          needed. Renaming one does NOT rewrite the employees already stored under the old label:
          their profile keeps showing it until someone edits them, so rename only when that is what
          you want.
        </p>
        <div className="grid gap-6 sm:grid-cols-2">
          <LabelListEditor
            legend="Designation levels"
            hint="The seniority half of a designation, e.g. Senior."
            values={levels}
            onChange={setLevels}
          />
          <LabelListEditor
            legend="Departments"
            hint="Which part of the business the employee works in."
            values={departments}
            onChange={setDepartments}
          />
        </div>
      </Section>

      {/*
        Record-keeping CC (compliance). Unlike the CC box in the composer, these
        are merged in SERVER-SIDE at send time, so an employee cannot drop one -
        which is the point: they are the audit copy, not a convenience.
      */}
      <Section icon={MailCheck} title="Record-Keeping CC">
        <p className="mb-4 text-sm text-ink/50">
          Addresses every outgoing email of that category is copied to, for the record. They are
          added by the server when the message is sent, so they appear as locked chips in the
          composer and cannot be removed by the sender. Leave a field blank to copy nobody for that
          category.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Quotation / Sales emails"
            type="text"
            placeholder="quotations@advisorkhoj.in"
            helperText="Copied on every quotation and other Sales-category email."
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('record_cc_sales')}
          />
          <Input
            label="Billing emails"
            type="text"
            placeholder="billing@advisorkhoj.com"
            helperText="Copied on tax invoices and other Billing-category email."
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('record_cc_billing')}
          />
          <Input
            label="Renewal emails"
            type="text"
            placeholder="billing@advisorkhoj.com"
            helperText="The renewal ladder is worked by the Billing desk, so this is usually the same address as Billing."
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('record_cc_renewal')}
          />
          <Input
            label="Service emails"
            type="text"
            placeholder="operations@advisorkhoj.in"
            helperText="Copied on service activation / termination notices."
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('record_cc_service')}
          />
          <div className="sm:col-span-2">
            <Input
              label="Internal emails"
              type="text"
              placeholder="Leave blank for none"
              helperText="Internal handovers between desks (e.g. the Sales RM's billing handover). Comma or semicolon separated, up to 10 addresses per field."
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('record_cc_internal')}
            />
          </div>
        </div>
      </Section>

      <Section icon={ShieldCheck} title="Compliance">
        <p className="mb-4 text-xs text-ink/50">
          Controls how the client accepts the Terms &amp; Conditions. These apply in production.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Controller
            name="tc_enforce_sent_before_accept"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Require T&C link to be emailed before it can be accepted"
                helperText="When On, the public T&C page only accepts a link that was actually emailed to the client, so a Sales RM can't reveal the link and accept it themselves. Turn Off only in dev to test acceptance quickly."
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
              >
                <option value="true">On</option>
                <option value="false">Off</option>
              </SelectField>
            )}
          />
        </div>
      </Section>

      <Section icon={Terminal} title="Developer">
        <p className="mb-4 text-xs text-ink/50">
          Local testing helpers. These have no effect in production.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Controller
            name="otp_delivery_mode"
            control={control}
            render={({ field }) => (
              <SelectField
                label="OTP delivery"
                helperText="How the login/signup OTP is delivered. 'Terminal' prints the raw code to the server log; 'Both' sends the SMS and prints it. Terminal/Both apply only outside production - production always sends SMS only and never logs the code."
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
              >
                <option value="sms">SMS only</option>
                <option value="terminal">Terminal only</option>
                <option value="both">Both (SMS + terminal)</option>
              </SelectField>
            )}
          />
        </div>
      </Section>

      <Card className="mt-6">
        <div className="mb-4 flex items-center justify-between border-b border-line pb-3">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-brand" />
            <p className="text-sm font-bold text-ink">Bank &amp; Payment Details</p>
          </div>
          <button
            type="button"
            onClick={onAddBank}
            className="flex items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink shadow-sm transition-colors hover:bg-cream"
          >
            <Plus className="h-3.5 w-3.5" />
            Add bank account
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {banks.map((bank, i) => (
            <button
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              type="button"
              onClick={() => openDialog(i)}
              className="group flex flex-col gap-3 rounded-xl border border-line bg-canvas/40 p-4 text-left transition-all hover:border-brand/40 hover:bg-brand/5 hover:shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line bg-white text-brand shadow-sm">
                  <Landmark className="h-4 w-4" />
                </span>
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink/30 transition-colors group-hover:bg-brand/10 group-hover:text-brand">
                  <Pencil className="h-3.5 w-3.5" />
                </span>
              </div>

              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-ink">
                  {bank.label || `Bank account ${i + 1}`}
                </p>
                {bank.bankName && (
                  <p className="mt-0.5 truncate text-xs text-ink/60">{bank.bankName}</p>
                )}
                {bank.accountNumber && (
                  <p className="mt-1 font-mono text-xs text-ink/40">
                    ••••
                    {bank.accountNumber.slice(-4)}
                  </p>
                )}
                {!bank.bankName && !bank.accountNumber && (
                  <p className="mt-0.5 text-xs italic text-ink/40">No details yet</p>
                )}
              </div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="mt-6">
        <div className="mb-4 flex items-center justify-between border-b border-line pb-3">
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-brand" />
            <p className="text-sm font-bold text-ink">Email Senders &amp; Support Addresses</p>
          </div>
          <button
            type="button"
            onClick={onAddSender}
            className="flex items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink shadow-sm transition-colors hover:bg-cream"
          >
            <Plus className="h-3.5 w-3.5" />
            Add sender
          </button>
        </div>
        <p className="mb-3 text-xs text-ink/50">
          One place for every company email - both the Mandrill From identities and the
          support/contact addresses shown inside templates. The Sales RM picks a From from this list
          (or their own email) when sending. Label identifies the purpose: a label matching a
          template&apos;s department (e.g. &quot;Sales RM&quot;) is the default From for it; labels
          containing &quot;Billing&quot;, &quot;MFBOX&quot;, &quot;Website&quot; or &quot;App&quot;
          fill the matching support-email template fields.
        </p>
        <div className="flex flex-col gap-2">
          <div className="hidden grid-cols-[1fr_1.4fr_1.2fr_auto] gap-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-ink/40 sm:grid">
            <span>Label (Dept)</span>
            <span>Email</span>
            <span>From Name</span>
            <span />
          </div>
          {senders.map((sender, i) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.4fr_1.2fr_auto] sm:items-center"
            >
              <Input
                placeholder="Sales RM"
                value={sender.label}
                onChange={(e) => onChangeSender(i, 'label', e.target.value)}
              />
              <Input
                placeholder="sales@advisorkhoj.com"
                value={sender.email}
                onChange={(e) => onChangeSender(i, 'email', e.target.value)}
              />
              <Input
                placeholder="Advisorkhoj Sales"
                value={sender.fromName}
                onChange={(e) => onChangeSender(i, 'fromName', e.target.value)}
              />
              <button
                type="button"
                onClick={() => onRemoveSender(i)}
                disabled={senders.length <= 1}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line text-ink/40 transition-colors hover:bg-danger-tint hover:text-danger disabled:opacity-40"
                aria-label="Remove sender"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </Card>

      <BankDialog
        bank={activeBank}
        open={dialogIndex !== null}
        canDelete={banks.length > 1}
        onClose={closeDialog}
        onSave={onSaveBank}
        onDelete={onDeleteBank}
      />
    </form>
  );
};

export default AdminCompanySettingsPage;
